/**
 * ListItem Component Definition
 *
 * A list item with an icon/image and text content.
 * Supports horizontal or vertical layout with customizable styling.
 */

export const listItemComponent = {
  type: 'listItem',
  label: 'List Item',
  icon: 'ListChecks',
  description: 'Icon/image with text for lists or feature highlights',
  category: 'content',

  canHaveChildren: false,

  resolveStyles(props) {
    const isVertical = props.direction === 'vertical';
    // Handle gap with unit support (backward compatible with number)
    let gapValue = '16px';
    if (typeof props.gap === 'object' && props.gap !== null) {
      if (props.gap.base) {
        const g = props.gap.base;
        gapValue = g.value === 'auto' ? '0px' : `${g.value}${g.unit || 'px'}`;
      } else {
        gapValue = props.gap.value === 'auto' ? '0px' : `${props.gap.value}${props.gap.unit || 'px'}`;
      }
    } else if (typeof props.gap === 'number') {
      gapValue = `${props.gap}px`;
    }

    return {
      display: 'flex',
      flexDirection: isVertical ? 'column' : 'row',
      alignItems: isVertical ? 'center' : 'flex-start',
      gap: gapValue,
    };
  },

  defaultProps: {
    // Content
    iconUrl: '',
    text: 'List item text',

    // Layout
    iconSize: 48,
    direction: 'horizontal',
    gap: { base: { value: 16, unit: 'px' }, tablet: null, desktop: null },

    // Typography
    fontFamily: 'System Default',
    fontSize: 16,
    fontWeight: 'normal',
    lineHeight: 1.5,
    letterSpacing: 0,
    textColor: '#1f2937',

    // Spacing
    padding: { base: { top: 8, bottom: 8, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },

    // Border
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 0,

    // Positioning
    position: 'static',
    top: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    right: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    bottom: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    left: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    zIndex: 'auto',

    // Custom CSS
    customCSS: '',

    // Flex child (when inside a flex parent) — responsive
    flexChild: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Content category
    {
      key: 'iconUrl',
      type: 'text',
      label: 'Icon/Image URL',
      category: 'Content',
      placeholder: 'https://example.com/icon.png',
    },
    {
      key: 'text',
      type: 'textarea',
      label: 'Text Content',
      category: 'Content',
      rows: 3,
    },

    // Layout category
    {
      key: 'iconSize',
      type: 'number',
      label: 'Icon Size (px)',
      category: 'Layout',
      min: 16,
      max: 200,
      default: 48,
    },
    {
      key: 'direction',
      type: 'select',
      label: 'Layout Direction',
      category: 'Layout',
      default: 'horizontal',
      options: [
        { value: 'horizontal', label: 'Horizontal (Icon Left)' },
        { value: 'vertical', label: 'Vertical (Icon Top)' },
      ],
    },
    {
      key: 'gap',
      type: 'responsiveDimension',
      label: 'Gap Between Icon and Text',
      category: 'Layout',
    },

    // Typography category
    {
      key: 'fontFamily',
      type: 'fontFamily',
      label: 'Font Family',
      category: 'Typography',
      default: 'System Default',
    },
    {
      key: 'fontSize',
      type: 'number',
      label: 'Font Size (px)',
      category: 'Typography',
      min: 8,
      max: 72,
      default: 16,
    },
    {
      key: 'fontWeight',
      type: 'select',
      label: 'Font Weight',
      category: 'Typography',
      default: 'normal',
      options: [
        { value: 'normal', label: 'Normal' },
        { value: 'medium', label: 'Medium' },
        { value: 'semibold', label: 'Semibold' },
        { value: 'bold', label: 'Bold' },
      ],
    },
    {
      key: 'lineHeight',
      type: 'number',
      label: 'Line Height',
      category: 'Typography',
      min: 1,
      max: 3,
      step: 0.1,
      default: 1.5,
    },
    {
      key: 'letterSpacing',
      type: 'number',
      label: 'Letter Spacing (px)',
      category: 'Typography',
      min: -2,
      max: 10,
      step: 0.1,
      default: 0,
    },
    {
      key: 'textColor',
      type: 'color',
      label: 'Text Color',
      category: 'Typography',
      default: '#1f2937',
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
    },

    // Border category
    {
      key: 'borderWidth',
      type: 'number',
      label: 'Border Width (px)',
      category: 'Border',
      min: 0,
      max: 20,
      default: 0,
    },
    {
      key: 'borderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
      ],
    },
    {
      key: 'borderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Border',
      min: 0,
      max: 100,
      default: 0,
    },

    // Position category
    {
      key: 'position',
      type: 'select',
      label: 'Position',
      category: 'Position',
      default: 'static',
      options: [
        { value: 'static', label: 'Static (Normal)' },
        { value: 'relative', label: 'Relative' },
        { value: 'absolute', label: 'Absolute' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'sticky', label: 'Sticky' },
      ],
    },
    {
      key: 'top',
      type: 'responsiveDimension',
      label: 'Top',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'right',
      type: 'responsiveDimension',
      label: 'Right',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'bottom',
      type: 'responsiveDimension',
      label: 'Bottom',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'left',
      type: 'responsiveDimension',
      label: 'Left',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'zIndex',
      type: 'number',
      label: 'Z-Index',
      category: 'Position',
      placeholder: 'auto',
      showWhen: { field: 'position', notEquals: 'static' },
    },

    // Advanced category
    {
      key: 'customCSS',
      type: 'customCSS',
      label: 'Custom CSS',
      category: 'Advanced',
      default: '',
    },

    // Flex Child sizing
    {
      key: 'flexChild',
      type: 'flexChild',
      label: 'Flex Child',
      category: 'Layout',
      showWhenParent: { field: 'display', in: ['flex', 'inline-flex'] },
      description: 'Controls sizing when inside a flex container',
      default: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
    },
  ],

  // Render function - will be implemented in the unified renderer
  render: null,
};

export default listItemComponent;
